import React from "react";
import { useLocation } from "react-router-dom";
import { company } from "../data/company";

// Lightweight JSON-LD injector (renders a script tag into document head on mount)
export const Schema = ({ data, id }) => {
  React.useEffect(() => {
    const tag = document.createElement("script");
    tag.type = "application/ld+json";
    tag.text = JSON.stringify(data);
    if (id) tag.dataset.schemaId = id;
    tag.dataset.koningSchema = "true";
    document.head.appendChild(tag);
    return () => {
      try { document.head.removeChild(tag); } catch (_) {}
    };
  }, [data, id]);
  return null;
};

// Centrale title-builder: houdt titles binnen ~50–68 tekens (Bing-max ~70).
// Volledige merknaam alleen wanneer er ruimte is, anders korte variant.
export const buildTitle = (base) => {
  const full = `${base} | Koning Aannemersbedrijf`;
  if (full.length <= 68) return full;
  const short = `${base} | Koning`;
  return short.length <= 70 ? short : base;
};

// Centrale description-clamper: max ~160 tekens, afgekapt op woordgrens.
export const buildDescription = (text, max = 160) => {
  const t = (text || "").trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max - 1);
  return cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:—–-]+$/, "") + "…";
};

// SEO Meta component — updates title, description, canonical, OG and Twitter tags
// Accepts `noindex` to force noindex on any single page (used for non-priority
// city pages that don't yet have unique local content).
export const Meta = ({ title, description, image, type = "website", noindex = false, canonicalPath }) => {
  const loc = useLocation();
  // Force the production non-www canonical for every page. Even when the site
  // is served from a preview / www.* / apex mirror, canonical always points at
  // the primary indexable URL. This prevents duplicate indexing and www forks.
  const canonicalBase = (company.url || "https://koningaannemers.nl").replace(/^https?:\/\/www\./i, "https://").replace(/\/$/, "");
  const path = (canonicalPath || loc.pathname || "/").replace(/\/+$/, "") || "/";
  const url = `${canonicalBase}${path === "/" ? "/" : path}`;
  const ogImage = image || `${canonicalBase}/brand/logo.svg`;

  React.useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content, attr = "name") => {
      if (!content) return;
      let m = document.querySelector(`meta[${attr}="${name}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute(attr, name);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:type", type, "property");
    setMeta("og:url", url, "property");
    setMeta("og:image", ogImage, "property");
    setMeta("og:site_name", company.name, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:url", url);
    setMeta("twitter:image", ogImage);

    // Canonical link — always the non-www, no trailing slash except on root.
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);

    // Robots directive: noindex the preview environment (any hostname that
    // is not the production canonical) and any admin/thank-you routes.
    const isProduction =
      typeof window !== "undefined" && window.location && window.location.hostname === "koningaannemers.nl";
    const isNoindexPath = /^\/(admin|login|bedankt|.*\/bedankt)/.test(path);
    const needsNoindex = noindex || !isProduction || isNoindexPath;
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.setAttribute("name", "robots");
      document.head.appendChild(robots);
    }
    robots.setAttribute("content", needsNoindex ? "noindex,follow" : "index,follow,max-image-preview:large");
  }, [title, description, url, ogImage, type, path, noindex]);

  return null;
};

// Breadcrumb JSON-LD schema helper
export const Breadcrumbs = ({ items }) => {
  // items: [{ name, url }]
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url?.startsWith("http") ? it.url : `${company.url}${it.url || ""}`,
    })),
  };
  return <Schema data={data} id="breadcrumbs" />;
};
